import { Text as LegacyText } from '@chakra-ui/react';
import {
  Box,
  Grid,
  HStack,
  Image,
  Text,
  VStack,
} from '@joinrepublic/dls-ui-kit';

import { InterDisplayStyles } from '../../config/styles';
import { MotionBox } from '../motion-box/motion-box';
import { MotionBoxGroup } from '../motion-box-group/motion-box-group';
import { IFounderSpotlight } from './types';

export interface IFounderSpotlightProps {
  founderSpotlight: IFounderSpotlight;
}

export const FounderSpotlight = ({
  founderSpotlight,
}: IFounderSpotlightProps) => {
  const {
    company,
    logo,
    industry,
    raised,
    backedBy,
    quote,
    author,
    authorPosition,
    authorPhoto,
  } = founderSpotlight;

  return (
    <MotionBoxGroup
      once={false}
      as="article"
      px={{ base: 4, sm: 0 }}
      color="white"
      cursor="default"
    >
      <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)' }} rowGap={6}>
        <Box as="figure">
          <Image
            src={authorPhoto.url}
            srcSet={`${authorPhoto.url} 1x, ${authorPhoto.url2x} 2x`}
            alt={`Photo of ${author}`}
            objectFit="cover"
            objectPosition={{ base: '50% 0%', sm: '50%' }}
            width="100%"
            height={{ base: '326px', sm: '580px' }}
            borderRadius={{ base: 'lg', md: 'xl' }}
          />
        </Box>

        <VStack
          align="start"
          gap={{ base: '8', sm: '12' }}
          pr={{ base: '0', lg: '8', xl: '16' }}
          pl={{ base: '0', sm: '8', lg: '16' }}
          minH={{ base: '316px', sm: 'auto' }}
        >
          <VStack
            gap={{ base: '4', sm: '10', md: '12' }}
            flexGrow={1}
            align="start"
          >
            <HStack as="header" gap={{ base: '3', md: '4' }}>
              <Image
                src={logo}
                alt={`${company} logo`}
                objectFit="cover"
                borderRadius={{ base: 'md', md: 'lg' }}
                boxSize={{ base: '48px', md: '68px' }}
              />
              <VStack gap={{ base: '0.5', md: '1' }} align="start">
                <LegacyText
                  as="h3"
                  fontSize={{ base: '36px', md: '50px' }}
                  fontWeight="400"
                  lineHeight="1em"
                  letterSpacing="-0.03em"
                  color="inherit"
                >
                  {company}
                </LegacyText>
                <LegacyText
                  fontSize={{ base: '9px', md: '14px' }}
                  lineHeight="1em"
                  color="inherit"
                  opacity={0.5}
                >
                  {industry}
                </LegacyText>
              </VStack>
            </HStack>

            <LegacyText as="figure" color="inherit">
              <MotionBox distance={15} once={false}>
                <LegacyText
                  as="blockquote"
                  fontSize={{
                    base: '18px',
                    sm: '24px',
                    lg: '32px',
                    xl: '35px',
                  }}
                  fontWeight="400"
                  lineHeight={{ base: '1.24em', md: '1.2em' }}
                  letterSpacing="-0.02em"
                  color="inherit"
                  position="relative"
                  sx={{
                    ...InterDisplayStyles,
                    textWrap: 'pretty',
                  }}
                  _before={{
                    content: '"“"',
                    position: 'absolute',
                    left: '-0.38em',
                  }}
                  _after={{
                    content: '"”"',
                  }}
                >
                  {quote}
                </LegacyText>

                <VStack
                  as="figcaption"
                  align="start"
                  gap={{ base: '1', md: '2' }}
                  mt={{ base: '4', md: '10' }}
                >
                  <LegacyText
                    fontSize={{ base: '12px', md: '24px' }}
                    lineHeight="1.333em"
                    letterSpacing={{ base: '-0.02em', md: '0' }}
                    fontWeight="600"
                    color="inherit"
                  >
                    {author}
                  </LegacyText>
                  <LegacyText
                    fontSize={{ base: '8px', md: '16px' }}
                    lineHeight="1em"
                    letterSpacing={{ base: '-0.01em', md: '-0.07em' }}
                    fontStyle="italic"
                    color="inherit"
                  >
                    {authorPosition}
                  </LegacyText>
                </VStack>
              </MotionBox>
            </LegacyText>
          </VStack>

          <HStack
            as="footer"
            gap={{ base: '8', md: '12' }}
            align="start"
            flexDirection={{ base: 'row', sm: 'column', md: 'row' }}
          >
            <MotionBox distance={10} delay={0.25} duration={0.3} once={false}>
              <VStack gap={{ base: '1', md: '3' }} align="start">
                <Text
                  as="h4"
                  size={{ base: 'body-xs' }}
                  textStyle={{ md: 'body-sm-md' }}
                  color="inherit"
                >
                  Raised to date
                </Text>
                <LegacyText
                  fontSize={{ base: '40px', md: '60px', xl: '74px' }}
                  letterSpacing="-0.06em"
                  lineHeight="1em"
                  color="inherit"
                  sx={{ fontFeatureSettings: "'calt', 'cv11'" }}
                >
                  {raised}
                </LegacyText>
              </VStack>
            </MotionBox>

            {backedBy && (
              <MotionBox distance={10} delay={0.4} duration={0.3} once={false}>
                <VStack gap={{ base: '0.5', md: '2', lg: '3' }} align="start">
                  <Text
                    as="h4"
                    size={{ base: 'body-xs' }}
                    textStyle={{ md: 'body-sm-md' }}
                    color="inherit"
                  >
                    Backed by
                  </Text>
                  <HStack gap={{ base: '5', sm: '10' }}>
                    {backedBy.map((backedByCompany) => (
                      <Box key={backedByCompany.name} as="figure">
                        <Image
                          src={backedByCompany.logo}
                          alt={`${backedByCompany.name} logo`}
                          height={{ base: '40px', sm: '60px', xl: '72px' }}
                          objectFit="contain"
                        />
                      </Box>
                    ))}
                  </HStack>
                </VStack>
              </MotionBox>
            )}
          </HStack>
        </VStack>
      </Grid>
    </MotionBoxGroup>
  );
};
